//Top100.js
(function () {
	'use strict';

	if (!document.body.classList.contains('page-template-template-top100')) return;

	const SPLIDE_JS = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js';
	const SPLIDE_CSS =
		'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide.min.css';

	let splide = null;
	let isMounted = false;
	const el = document.getElementById('top100-splide');

	/* --------------------------------
	   HELPERS
	-------------------------------- */
	function hasResource(value, tag, attr) {
		return [...document.querySelectorAll(`${tag}[${attr}]`)].some(el =>
			(el.getAttribute(attr) || '').includes(value)
		);
	}

	function loadCSS(cb) {
		if (hasResource('splide.min.css', 'link', 'href')) return cb();
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = SPLIDE_CSS;
		link.onload = cb;
		document.head.appendChild(link);
	}

	function loadJS(cb) {
		if (window.Splide) return cb();
		const script = document.createElement('script');
		script.src = SPLIDE_JS;
		script.onload = cb;
		document.body.appendChild(script);
	}

	function normalize(str = '') {
		return str
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, '')
			.replace(/\s+/g, ' ')
			.trim();
	}

	function extractRank(title = '') {
		const m = title.match(/rank\s*(\d+)/i);
		return m ? parseInt(m[1], 10) : null;
	}

	function extractName(title = '') {
		return normalize(title.replace(/rank\s*\d+/i, ''));
	}

	function parseSearchRank(raw = '') {
		const m = raw.match(/rank\s*(\d+)/i);
		if (m) return parseInt(m[1], 10);
		if (/^\d+$/.test(raw.trim())) return parseInt(raw.trim(), 10);
		return null;
	}

	/* --------------------------------
	   SORT SLIDES
	-------------------------------- */
	function sortSlides() {
		const list = el.querySelector('.splide__list');
		if (!list) return;

		const slides = [...list.children];
		slides.sort(
			(a, b) =>
				(extractRank(a.dataset.title) ?? 9999) - (extractRank(b.dataset.title) ?? 9999)
		);

		slides.forEach((slide, i) => {
			slide.dataset.index = i;
			list.appendChild(slide);
		});
	}

	function getSlideIndexByRank(rank) {
		const slides = el.querySelectorAll('.splide__slide');
		for (const slide of slides) {
			if (extractRank(slide.dataset.title) === rank) {
				return Number(slide.dataset.index);
			}
		}
		return 0;
	}

	/* --------------------------------
	   STACK
	-------------------------------- */
	function updateStackClasses() {
		if (!splide) return;

		const Slides = splide.Components.Slides.get();
		const total = splide.length;
		const active = splide.index;

		Slides.forEach(S => {
			const el = S.slide;
			el.classList.remove('left-stack', 'right-stack');
			el.style.zIndex = '';
		});

		Slides.forEach(S => {
			if (S.index === active) {
				S.slide.style.zIndex = 4000;
			}
		});

		Slides.forEach(S => {
			if (S.index === active) return;

			let diff = S.index - active;
			if (diff > total / 2) diff -= total;
			if (diff < -total / 2) diff += total;

			const side = diff > 0 ? 'right-stack' : 'left-stack';
			const depth = Math.abs(diff);

			S.slide.classList.add(side);
			S.slide.style.zIndex = 3000 - depth;
		});
	}

	function centerSlideByRank(rank) {
		if (!splide || !isMounted) return;

		const targetIndex = getSlideIndexByRank(rank);
		if (splide.index === targetIndex) return;

		splide.go(targetIndex);
	}

	/* --------------------------------
	   SLIDER INIT
	-------------------------------- */
	function getPerPage() {
		const w = window.innerWidth;
		let p = w <= 575 ? 3 : w <= 767 ? 5 : w <= 1023 ? 9 : 11;
		return p % 2 === 0 ? p + 1 : p;
	}

	function preloadImages() {
		if (!el) return Promise.resolve();

		const slides = el.querySelectorAll('.splide__slide');
		const perPage = getPerPage();
		const visibleSlides = Math.min(perPage + 2, slides.length); // Load visible + 2 extra
		
		const imagesToLoad = [];
		
		// Preload first rank (rank 1) and surrounding visible slides
		for (let i = 0; i < visibleSlides && i < slides.length; i++) {
			const img = slides[i].querySelector('img');
			if (img && img.dataset.src) {
				imagesToLoad.push(
					new Promise((resolve) => {
						const tempImg = new Image();
						tempImg.onload = () => {
							img.src = img.dataset.src;
							resolve();
						};
						tempImg.onerror = resolve; // Continue even if image fails
						tempImg.src = img.dataset.src;
					})
				);
			} else if (img && img.src) {
				// Image already has src, just ensure it's loaded
				if (img.complete) {
					imagesToLoad.push(Promise.resolve());
				} else {
					imagesToLoad.push(
						new Promise((resolve) => {
							img.onload = resolve;
							img.onerror = resolve;
						})
					);
				}
			}
		}

		return Promise.all(imagesToLoad);
	}

	function lazyLoadRemainingImages() {
		if (!el) return;

		const slides = el.querySelectorAll('.splide__slide');
		const perPage = getPerPage();
		
		// Lazy load remaining images after initial visible ones
		slides.forEach((slide, index) => {
			if (index >= perPage + 2) {
				const img = slide.querySelector('img');
				if (img && img.dataset.src && !img.src) {
					// Use Intersection Observer for true lazy loading
					const observer = new IntersectionObserver((entries) => {
						entries.forEach(entry => {
							if (entry.isIntersecting) {
								const image = entry.target;
								if (image.dataset.src) {
									image.src = image.dataset.src;
								}
								observer.unobserve(image);
							}
						});
					}, { rootMargin: '100px' });
					
					observer.observe(img);
				}
			}
		});
	}

	function initSlider() {
		if (!el) return;

		sortSlides();

		// Show loading state
		el.style.opacity = '0.5';

		preloadImages().then(() => {
			splide = new Splide(el, {
				type: 'loop',
				focus: 'center',
				perPage: getPerPage(),
				interval: 8000,
				speed: 0,
				easing: 'linear',
				drag: true,
				pagination: false,
				arrows: false,
				flickPower: 300,
				dragMinThreshold: 10,
				lazyLoad: false, // We're handling lazy loading manually
			});

			splide.on('mounted moved resized updated', () => {
				isMounted = true;
				updateStackClasses();
				bindSlideClicks();
			});

			splide.mount();
			centerSlideByRank(1);
			
			// Fade in after images loaded
			el.style.opacity = '1';
			
			// Load remaining images in background
			setTimeout(lazyLoadRemainingImages, 100);
		});
	}

	/* --------------------------------
	   SEARCH (FINAL FIX)
	-------------------------------- */
	function bindSearch() {
		const input = document.getElementById('cardSearch');
		if (!input) return;

		let timer;

		input.addEventListener('input', e => {
			clearTimeout(timer);

			timer = setTimeout(() => {
				const raw = e.target.value || '';
				const value = normalize(raw);
				const slides = el.querySelectorAll('.splide__slide');

				// RESET STATES
				if (!value || value === 'rank') {
					slides.forEach(s => (s.style.opacity = ''));
					centerSlideByRank(1);
					return;
				}

				const searchRank = parseSearchRank(raw);
				const matchedRanks = [];

			slides.forEach(slide => {
				const title = slide.dataset.title || '';
				const name = extractName(title);
				const rank = extractRank(title);

				let isMatch = false;

				if (searchRank !== null) {
					isMatch = rank === searchRank;
				} else if (value.length >= 3 && value !== 'rank') {
					isMatch = name.startsWith(value);
				}

				// Don't change opacity - removed blur effect

				if (isMatch && rank !== null) {
					matchedRanks.push(rank);
				}
			});

				if (!matchedRanks.length) {
					slides.forEach(s => (s.style.opacity = ''));
					centerSlideByRank(1);
					return;
				}

				centerSlideByRank(Math.min(...matchedRanks));
			}, 150);
		});
	}

	/* --------------------------------
	   TABS
	-------------------------------- */
	function bindTabs() {
		const tabs = document.querySelectorAll('.top100__tabs .tab');
		const panels = document.querySelectorAll('.tab-content');

		tabs.forEach(tab => {
			tab.addEventListener('click', () => {
				tabs.forEach(t => t.classList.remove('active'));
				tab.classList.add('active');

				panels.forEach(p => {
					p.style.opacity = '0';
					setTimeout(() => {
						p.classList.remove('active');
						p.style.pointerEvents = 'none';
					}, 300);
				});

				const panel = document.getElementById(tab.dataset.tab);
				if (panel) {
					panel.classList.add('active');
					panel.style.pointerEvents = 'auto';
					requestAnimationFrame(() => {
						panel.style.opacity = '1';
					});
				}
			});
		});
	}

	/* --------------------------------
	   SLIDE CLICK
	-------------------------------- */
	function bindSlideClicks() {
		if (!splide || !isMounted) return;

		const Slides = splide.Components.Slides.get();
		
		Slides.forEach(S => {
			const slide = S.slide;
			slide.style.cursor = 'pointer';
			
			slide.onclick = (e) => {
				e.preventDefault();
				e.stopPropagation();
				
				if (S.index !== splide.index) {
					splide.go(S.index);
				}
			};
		});
	}

	/* --------------------------------
	   BOOT
	-------------------------------- */
	document.addEventListener('DOMContentLoaded', () => {
		loadCSS(() =>
			loadJS(() => {
				initSlider();
				bindSearch();
				bindTabs();
			})
		);
	});
})();
